import React, { useState } from "react";
import { leadDisplayName, leadInitials } from "./utils";

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <span className="text-slate-400 text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open ? <div className="px-4 pb-4">{children}</div> : null}
    </div>
  );
}

export default function ContactPanel({ threadSummary, leadContext }) {
  if (!threadSummary) {
    return <div className="h-full border-l border-slate-200 bg-white hidden lg:block" />;
  }

  const lead = threadSummary.lead;
  const tags = leadContext?.tags || [];
  const activities = leadContext?.activities || [];
  const headline = leadContext?.headline || lead?.jobTitle;
  const talkingFrom = leadContext?.talkingFrom?.accountName;

  return (
    <div className="h-full border-l border-slate-200 bg-white overflow-y-auto hidden lg:flex lg:flex-col">
      <div className="px-4 py-4 flex items-start gap-3 border-b border-slate-100">
        {lead?.profileImageUrl ? (
          <img
            src={lead.profileImageUrl}
            alt=""
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold">
            {leadInitials(lead)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">
            {leadDisplayName(lead)}
          </div>
          {lead?.company ? (
            <div className="text-xs text-slate-500 truncate">{lead.company}</div>
          ) : null}
          {leadContext?.linkedinUrl || lead?.linkedinUrl ? (
            <a
              href={leadContext?.linkedinUrl || lead?.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              LinkedIn profile
            </a>
          ) : null}
        </div>
      </div>

      <Section title="Auto-tag">
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            Generic
          </span>
          {tags.slice(0, 6).map((tag) => (
            <span
              key={tag}
              className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700"
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          {tags.length
            ? `Auto-tagged in ${tags.join(" · ")}`
            : "No auto-tags yet"}
          {talkingFrom ? `, talking from ${talkingFrom}` : ""}.
        </p>
      </Section>

      <Section title="Tags">
        {tags.length === 0 ? (
          <p className="text-xs text-slate-400">No tags</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section title="Activity">
        {activities.length === 0 ? (
          <p className="text-xs text-slate-400">No recent activity</p>
        ) : (
          <ul className="space-y-2">
            {activities.slice(0, 5).map((activity) => (
              <li key={activity.id} className="text-xs text-slate-600">
                <div className="font-medium text-slate-700">{activity.type}</div>
                <div className="text-slate-500 line-clamp-2">
                  {activity.description}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Headline">
        <p className="text-xs text-slate-600 leading-relaxed">
          {headline || leadContext?.location || "No headline available"}
        </p>
        {leadContext?.location ? (
          <p className="text-[11px] text-slate-400 mt-2">{leadContext.location}</p>
        ) : null}
      </Section>
    </div>
  );
}
